import {
  CursorArrowRaysIcon,
  Dialog,
  Editable,
  Flex,
  IconButton,
  Menu,
  Panel,
  PlusIcon,
  Tooltip,
  TreeView,
  type TreeViewNode,
} from 'components'
import { isEqual, startCase } from 'lodash-es'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ILayout } from 'types'
import { useScrolling } from 'utils'
import {
  LayoutWithKeyPath,
  containerLayoutTypes,
  getRoutingKeys,
  iconByLayoutType,
  keyFromPath,
} from '../../../utils/templateBuilderUtils'
import { useTemplate } from '../TemplateProvider'
import LayoutSelect from './LayoutSelect'
import { useTemplateLayout } from './TemplateLayoutProvider'

interface Props {
  initialRootLayoutKey?: number
}

export default function LayoutTree({ initialRootLayoutKey }: Props) {
  const { template, updateTemplate } = useTemplate()
  const { rootLayout, selectedLayout, setRootLayoutKey, setSelectedKeyPath } = useTemplateLayout()
  const { contentRef, overflowRef, scrolling } = useScrolling()
  const [addChildKeyPath, setAddChildKeyPath] = useState<string>()
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])

  const handleRename = (keyPath: string, newLabel: string) => {
    if (!template?.layoutMap) return

    const key = keyFromPath(keyPath)
    const layout = template.layoutMap[key]
    if (!layout) return

    const updatedLayoutMap = {
      ...template.layoutMap,
      [key]: { ...layout, label: newLabel },
    }

    updateTemplate({ layoutMap: updatedLayoutMap })
  }

  const routing = useRef(template?.routing)
  if (!isEqual(routing.current, template?.routing)) routing.current = template?.routing

  const rootKeys = useMemo(() => {
    if (!routing.current) return []

    const { layoutKeys } = getRoutingKeys(routing.current)
    const shortcutRoot = template?.layoutShortcut?.key
    return shortcutRoot ? [...layoutKeys, shortcutRoot] : layoutKeys
  }, [routing.current])

  useEffect(() => {
    if (initialRootLayoutKey != null && rootKeys.includes(initialRootLayoutKey)) {
      setRootLayoutKey(initialRootLayoutKey)
      return
    }
    setRootLayoutKey(rootKeys[0])
  }, [initialRootLayoutKey, rootKeys, setRootLayoutKey])

  const layoutMap = useRef(template?.layoutMap)
  if (!isEqual(layoutMap.current, template?.layoutMap)) layoutMap.current = template?.layoutMap

  const treeData = useMemo(
    () =>
      toTree(layoutMap.current, rootLayout, setSelectedKeyPath, handleRename, setAddChildKeyPath),
    [layoutMap.current, rootLayout]
  )

  useEffect(() => {
    setSelectedKeyPath(rootLayout?.keyPath)
    setExpandedKeys(treeData?.keyPaths ?? [])
  }, [rootLayout?.keyPath])

  return (
    <Panel gap={2} width="300px" overflow="hidden" pt={4}>
      <Menu
        mx={4}
        options={rootKeys.map(key => {
          const layout = template?.layoutMap?.[key]
          if (!layout) return { value: key }
          return { label: layout.label || startCase(layout.type), value: key }
        })}
        value={rootLayout && parseInt(rootLayout.keyPath)}
        onChange={setRootLayoutKey}
        menuProps={{ positioning: { sameWidth: true } }}
        placeholder="Select root layout"
      />

      <Flex
        flex={1}
        borderTopWidth={scrolling ? '1px' : '0'}
        ref={overflowRef}
        pt={2}
        overflow="auto"
      >
        <TreeView
          ref={contentRef}
          onRearrange={(...args) =>
            updateTemplate({ layoutMap: sortLayoutMap(template?.layoutMap, ...args) })
          }
          expandedValue={expandedKeys.map(key => key.toString())}
          onExpandedChange={({ expandedValue }) => setExpandedKeys(expandedValue)}
          selectedValue={selectedLayout?.keyPath ? [selectedLayout.keyPath] : []}
          onSelectionChange={({ focusedValue }) => setSelectedKeyPath(focusedValue || undefined)}
          data={treeData.data ?? []}
        />
      </Flex>

      <Dialog
        bodyProps={{ p: 6 }}
        title="Add Child Layout"
        open={!!addChildKeyPath}
        onClose={() => setAddChildKeyPath(undefined)}
      >
        <LayoutSelect
          parentKeyPath={addChildKeyPath}
          onSelect={l => {
            setAddChildKeyPath(undefined)
            if (!l) return

            setSelectedKeyPath(l.keyPath)
            if (expandedKeys.indexOf(addChildKeyPath!) === -1)
              setExpandedKeys([...expandedKeys, addChildKeyPath!])
          }}
        />
      </Dialog>
    </Panel>
  )
}

function toTree(
  layoutMap?: Record<number, ILayout>,
  rootLayout?: LayoutWithKeyPath,
  onSelect?: (keyPath?: string) => void,
  onRename?: (keyPath: string, newLabel: string) => void,
  onShowAddChild?: (parentKeyPath: string) => void
) {
  if (!layoutMap || !rootLayout?.keyPath) return {}

  const visited = new Set<string>()

  const buildNode = (keyPath: string) => {
    if (visited.has(keyPath)) return
    visited.add(keyPath)

    const pathKeys = keyPath?.split('/')
    const lastKey = pathKeys?.[pathKeys.length - 1]
    const layout = layoutMap[parseInt(lastKey)]
    if (!layout) return

    let isAction = false
    if (pathKeys.length > 1) {
      const parentKey = pathKeys[pathKeys.length - 2]
      const parentLayout = layoutMap[parseInt(parentKey)]
      if (parentLayout?.action?.layoutKey === parseInt(lastKey)) isAction = true
    }

    const Icon = iconByLayoutType[layout.type]
    const isContainer = containerLayoutTypes.includes(layout.type)

    let _children: (TreeViewNode | undefined)[] = []
    if (layout.action) {
      _children.push(buildNode(`${keyPath}/${layout.action.layoutKey}`))
    }
    if ('layouts' in layout) {
      const layouts = layout.layouts.map(({ key }) =>
        key != null ? buildNode(`${keyPath}/${key}`) : undefined
      )
      _children = [..._children, ...layouts]
    }
    const children = _children.filter(Boolean) as TreeViewNode[]

    return {
      value: keyPath,
      label: (
        <Flex alignItems="center" color={isAction ? 'secondary.500' : undefined} gap={1}>
          <Icon size="sm" />
          <Editable
            size="sm"
            activationMode="dblclick"
            value={layout.label}
            defaultValue={startCase(layout.type)}
            onValueChange={({ value }) => onRename?.(keyPath, value)}
            previewProps={{ textStyle: 'bodyMedium', cursor: 'pointer', whiteSpace: 'nowrap' }}
            inputProps={{ textStyle: 'bodyMedium' }}
          />
          {layout.action && (
            <Tooltip content="Action item">
              <CursorArrowRaysIcon justifySelf="flex-end" color="secondary.500" />
            </Tooltip>
          )}
          {onShowAddChild && (isContainer || !layout.action) && (
            <IconButton
              size="sm"
              icon={<PlusIcon size="sm" />}
              aria-label="Add child layout"
              justifySelf="flex-end"
              color="secondary.500"
              onClick={() => onShowAddChild(keyPath)}
            />
          )}
        </Flex>
      ),
      disableDrag: isAction,
      onClick: () => onSelect?.(keyPath),
      ...(children.length && { children }),
    } as TreeViewNode
  }

  const rootNode = buildNode(rootLayout.keyPath)
  if (!rootNode) return {}

  return { data: [rootNode], keyPaths: Array.from(visited) }
}

function sortLayoutMap(
  layoutMap?: Record<number, ILayout>,
  nodeValue?: string,
  parentValue?: string,
  childIndex?: number
): Record<number, ILayout> | undefined {
  if (!layoutMap || !nodeValue || !parentValue || childIndex == null) {
    return layoutMap
  }

  const updatedLayoutMap = { ...layoutMap }

  const nodeKeys = nodeValue.split('/').map(k => parseInt(k))
  const movedLayoutKey = nodeKeys[nodeKeys.length - 1]

  const parentKeys = parentValue.split('/').map(k => parseInt(k))
  const newParentKey = parentKeys[parentKeys.length - 1]

  // Remove the layout from its current parent
  if (nodeKeys.length > 1) {
    const currentParentKey = nodeKeys[nodeKeys.length - 2]
    const currentParentLayout = updatedLayoutMap[currentParentKey]

    if (currentParentLayout && 'layouts' in currentParentLayout) {
      updatedLayoutMap[currentParentKey] = {
        ...currentParentLayout,
        layouts: currentParentLayout.layouts.filter(item => item.key !== movedLayoutKey),
      }
    }
  }

  // Add the layout to the new parent at the specified index
  const newParentLayout = updatedLayoutMap[newParentKey]
  if (newParentLayout && 'layouts' in newParentLayout) {
    const layouts = [...newParentLayout.layouts]
    layouts.splice(childIndex, 0, { key: movedLayoutKey })

    updatedLayoutMap[newParentKey] = {
      ...newParentLayout,
      layouts,
    }
  }

  return updatedLayoutMap
}
